import { useEffect, useMemo } from 'react'
import { Field, Form, Formik, type FormikHelpers } from 'formik'
import { useNavigate, useSearchParams } from 'react-router-dom'
import logoMark from '@assets/logo_mark.svg'
import { useAuthStore } from '@stores/authStore'
import { type LoginPayload } from '@app-types/api/auth'
import {
  loginButtonFocusClass,
  loginFieldClass,
  loginInputClass,
  SAML_LOGIN_ACTION,
} from './constants'
import { routePathFromRedirectUrl } from './helpers'

function LoginPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const session = useAuthStore((state) => state.session)
  const sessionError = useAuthStore((state) => state.error)
  const loadSession = useAuthStore((state) => state.loadSession)
  const loginSession = useAuthStore((state) => state.loginSession)

  const nextUrl = searchParams.get('next') || '/'
  const loginError = searchParams.get('error')
  const csrfToken = session?.csrfToken || ''

  const samlUrl = useMemo(() => {
    const samlParams = new URLSearchParams({ next: nextUrl })
    return `${SAML_LOGIN_ACTION}?${samlParams.toString()}`
  }, [nextUrl])

  useEffect(() => {
    void loadSession({ force: true })
  }, [loadSession])

  const handleSubmit = async (
    values: LoginPayload,
    helpers: FormikHelpers<LoginPayload>,
  ) => {
    if (!csrfToken) {
      return
    }

    helpers.setStatus(undefined)

    try {
      const result = await loginSession(values, csrfToken)

      if (result.success) {
        navigate(routePathFromRedirectUrl(result.redirectUrl || nextUrl))
        return
      }

      helpers.setStatus(
        result.error === 'csrf_failed'
          ? 'Не удалось проверить CSRF-токен, обновите страницу'
          : 'Неверный логин или пароль',
      )
    } catch {
      helpers.setStatus('Не удалось выполнить вход')
    } finally {
      helpers.setSubmitting(false)
    }
  }

  return (
    <main className="grid min-h-svh place-items-center bg-gradient-to-br from-white via-slate-50 to-emerald-50 px-5 py-10 text-slate-600 dark:from-zinc-950 dark:via-zinc-950 dark:to-emerald-950/40 dark:text-slate-400 max-[520px]:place-items-start max-[520px]:px-4 max-[520px]:py-6">
      <section
        className="w-full max-w-[420px]"
        aria-labelledby="login-title"
      >
        <div className="mb-5 flex items-center gap-4 text-left max-[520px]:mt-3 max-[520px]:items-start">
          <img
            className="size-14 shrink-0 rounded-xl shadow-lg shadow-slate-950/10"
            src={logoMark}
            width="56"
            height="56"
            alt=""
            aria-hidden="true"
          />
          <div>
            <p className="mb-1 text-[13px] leading-tight text-slate-500 dark:text-slate-400">
              Games Revenue Statistics
            </p>
            <h1
              id="login-title"
              className="m-0 text-3xl leading-tight font-semibold tracking-normal text-slate-950 dark:text-slate-100"
            >
              Вход в grstat
            </h1>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white/95 p-7 text-left shadow-xl shadow-slate-950/10 dark:border-zinc-800 dark:bg-zinc-900/95 dark:shadow-black/40 max-[520px]:p-5">
          {sessionError ? (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
              {sessionError}
            </div>
          ) : null}

          <Formik<LoginPayload>
            enableReinitialize
            initialValues={{
              username: '',
              password: '',
              next: nextUrl,
            }}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, status }) => (
              <Form className="grid gap-4">
                {loginError === 'invalid_login' || status ? (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                    {status || 'Неверный логин или пароль'}
                  </div>
                ) : null}

                <input
                  type="hidden"
                  name="csrfmiddlewaretoken"
                  value={csrfToken}
                />
                <Field type="hidden" name="next" />

                <label className={loginFieldClass}>
                  <span>Логин</span>
                  <Field
                    autoComplete="username"
                    className={loginInputClass}
                    autoFocus
                    name="username"
                    placeholder="username или email"
                    required
                    type="text"
                  />
                </label>

                <label className={loginFieldClass}>
                  <span>Пароль</span>
                  <Field
                    autoComplete="current-password"
                    className={loginInputClass}
                    name="password"
                    placeholder="Введите пароль"
                    required
                    type="password"
                  />
                </label>

                <button
                  className={`inline-flex min-h-11 cursor-pointer items-center justify-center rounded-md border border-slate-900 bg-slate-900 px-4 text-[15px] leading-none font-bold text-white transition hover:border-slate-800 hover:bg-slate-800 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300 disabled:text-slate-500 dark:border-slate-50 dark:bg-slate-50 dark:text-slate-950 dark:hover:border-slate-200 dark:hover:bg-slate-200 dark:disabled:border-zinc-700 dark:disabled:bg-zinc-800 dark:disabled:text-zinc-500 ${loginButtonFocusClass}`}
                  disabled={!csrfToken || !!sessionError || isSubmitting}
                  type="submit"
                >
                  {isSubmitting
                    ? 'Вход...'
                    : csrfToken
                      ? 'Войти'
                      : 'Подготовка...'}
                </button>
              </Form>
            )}
          </Formik>

          <div className="my-5 flex items-center gap-3 text-[13px] text-slate-500 dark:text-slate-400">
            <span className="h-px flex-1 bg-slate-200 dark:bg-zinc-800" />
            <span>или</span>
            <span className="h-px flex-1 bg-slate-200 dark:bg-zinc-800" />
          </div>

          <a
            className={`inline-flex min-h-11 w-full cursor-pointer items-center justify-center rounded-md border border-slate-200 bg-white px-4 text-[15px] leading-none font-bold text-slate-950 no-underline transition hover:border-violet-400 hover:shadow-lg hover:shadow-slate-950/10 dark:border-zinc-700 dark:bg-zinc-950 dark:text-slate-100 dark:hover:border-violet-400 dark:hover:shadow-black/30 ${loginButtonFocusClass}`}
            href={samlUrl}
          >
            Войти через SAML
          </a>
        </div>
      </section>
    </main>
  )
}

export default LoginPage
